import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import IconPalette from './IconPalette';
import { AuthContext } from '../context/AuthContext';
import api from '../utils/api';
import './DynamicDataCollectionForm.css';

/**
 * DynamicDataCollectionForm Component
 * 
 * Universal form that renders based on data type configuration.
 * Handles:
 * - Icon palette selection for standardized data
 * - Structured fields (text, select, date, checkbox, etc.)
 * - Free-text fields for human observations
 * - File uploads
 * - Map data (GeoJSON)
 * - Location picker integration
 * 
 * Props:
 * - dataTypeConfig: Configuration object from dataTypes.js
 * - initialData: Existing data for editing (optional)
 * - onSubmit: Callback when form is submitted
 * - onCancel: Callback when form is cancelled
 */
const DynamicDataCollectionForm = ({ 
  dataTypeConfig, 
  initialData = null, 
  onSubmit, 
  onCancel 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useContext(AuthContext);

  // Form state
  const [selectedIcon, setSelectedIcon] = useState(initialData?.structuredData?.selectedIcon || null);
  const [structuredData, setStructuredData] = useState(initialData?.structuredData || {});
  const [freeTextData, setFreeTextData] = useState(initialData?.freeTextData || {});
  const [mapData, setMapData] = useState(initialData?.mapData || '');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [harvestCategories, setHarvestCategories] = useState([]);
  const [harvestCategoriesLoading, setHarvestCategoriesLoading] = useState(false);

  const isHarvestingForm = dataTypeConfig?.id === 'harvesting';

  const normalizeString = useCallback((value = '') => value.trim().replace(/\s+/g, ' '), []);

  const buildHarvestCategoryIcon = useCallback((category) => {
    const normalizedName = normalizeString(category?.name || 'Other');
    return {
      id: `HARVEST_${normalizedName.toUpperCase().replace(/[^A-Z0-9]+/g, '_')}`,
      icon: category?.icon || dataTypeConfig.icon || '🌿',
      label: normalizedName,
      color: dataTypeConfig.color || '#2E7D32'
    };
  }, [dataTypeConfig.color, dataTypeConfig.icon, normalizeString]);

  const buildFallbackHarvestCategories = useCallback(() => {
    const palette = dataTypeConfig?.iconPalette;
    if (!palette || typeof palette !== 'object' || Array.isArray(palette)) return [];

    return Object.entries(palette)
      .filter(([name]) => name !== 'default')
      .map(([name, icon]) => ({ name, icon, subcategories: [] }));
  }, [dataTypeConfig?.iconPalette]);

  const selectedHarvestCategoryName = normalizeString(structuredData?.category || selectedIcon?.label || '');
  const selectedHarvestCategory = isHarvestingForm
    ? harvestCategories.find((category) => normalizeString(category?.name || '') === selectedHarvestCategoryName)
    : null;
  const selectedSubcategoryName = normalizeString(structuredData?.subcategory || '');
  const availableSubcategories = useMemo(() => {
    if (!Array.isArray(selectedHarvestCategory?.subcategories)) return [];
    return selectedHarvestCategory.subcategories
      .map((subcategory) => (typeof subcategory === 'string' ? { name: subcategory } : subcategory))
      .filter((subcategory) => normalizeString(subcategory?.name || '').length > 0);
  }, [selectedHarvestCategory, normalizeString]);

  const setHarvestCategory = useCallback((categoryName) => {
    const cleanedCategory = normalizeString(categoryName);
    const matchedCategory = harvestCategories.find(
      (category) => normalizeString(category?.name || '') === cleanedCategory
    );

    setStructuredData((prev) => ({
      ...prev,
      category: cleanedCategory,
      subcategory: ''
    }));

    if (matchedCategory) {
      setSelectedIcon(buildHarvestCategoryIcon(matchedCategory));
      return;
    }

    if (!cleanedCategory) {
      setSelectedIcon(null);
      return;
    }

    setSelectedIcon({
      id: `HARVEST_${cleanedCategory.toUpperCase().replace(/[^A-Z0-9]+/g, '_')}`,
      icon: dataTypeConfig.icon || '🌿',
      label: cleanedCategory,
      color: dataTypeConfig.color || '#2E7D32'
    });
  }, [buildHarvestCategoryIcon, dataTypeConfig.color, dataTypeConfig.icon, harvestCategories, normalizeString]);

  const setHarvestSubcategory = useCallback((subcategoryName) => {
    setStructuredData((prev) => ({
      ...prev,
      subcategory: normalizeString(subcategoryName)
    }));
  }, [normalizeString]);

  // Harvesting category management has moved to CIP; use the local icon palette only.
  useEffect(() => {
    if (!isHarvestingForm) return;
    setHarvestCategories(buildFallbackHarvestCategories());
  }, [buildFallbackHarvestCategories, isHarvestingForm]);

  useEffect(() => {
    if (!isHarvestingForm) return;
    if (!selectedHarvestCategoryName || !selectedHarvestCategory) return;

    const iconMissing = !selectedIcon?.icon;
    const labelMismatch = normalizeString(selectedIcon?.label || '') !== selectedHarvestCategoryName;
    if (iconMissing || labelMismatch) {
      setSelectedIcon(buildHarvestCategoryIcon(selectedHarvestCategory));
    }
  }, [
    buildHarvestCategoryIcon,
    isHarvestingForm,
    selectedHarvestCategory,
    selectedHarvestCategoryName,
    selectedIcon?.icon,
    selectedIcon?.label,
    normalizeString,
  ]);

  useEffect(() => {
    if (!isHarvestingForm) return;
    if (!selectedSubcategoryName) return;

    const stillValid = availableSubcategories.some(
      (subcategory) => normalizeString(subcategory.name || '') === selectedSubcategoryName
    );

    if (!stillValid) {
      setHarvestSubcategory('');
    }
  }, [isHarvestingForm, selectedSubcategoryName, availableSubcategories, normalizeString, setHarvestSubcategory]);

  // Handle returning from location picker
  useEffect(() => {
    if (location.state?.geoJsonData) {
      setMapData(location.state.geoJsonData);
      // Clear the location state
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Update structured data field
  const handleStructuredFieldChange = (fieldName, value) => {
    setStructuredData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  // Update free-text data field
  const handleFreeTextFieldChange = (fieldName, value) => {
    setFreeTextData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  // Handle file selection
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  // Open location picker
  const openLocationPicker = () => {
    let parsedMapData = null;
    if (mapData) {
      try {
        parsedMapData = typeof mapData === 'string' ? JSON.parse(mapData) : mapData;
      } catch (err) {
        console.error('Error parsing map data:', err);
      }
    }

    navigate(`/location-picker`, {
      state: {
        returnTo: location.pathname,
        existingGeoJson: parsedMapData,
        dataType: dataTypeConfig.id
      }
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isHarvestingForm && !selectedHarvestCategoryName) {
      setError('Please select a harvesting category.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Build complete data object
      const formData = {
        dataType: dataTypeConfig.id,
        structuredData: {
          ...structuredData,
          selectedIcon: selectedIcon
        },
        freeTextData,
        mapData,
        createdBy: currentUser?._id,
        createdByName: currentUser?.name || 'Anonymous'
      };

      // If file upload is enabled and file is selected
      if (dataTypeConfig.fileUpload?.enabled && file) {
        // Create FormData for file upload
        const uploadData = new FormData();
        uploadData.append('file', file);
        uploadData.append('data', JSON.stringify(formData));
        
        await onSubmit(uploadData, true); // true indicates FormData
      } else {
        await onSubmit(formData, false);
      }

      // Reset form on success
      setSelectedIcon(null);
      setStructuredData({});
      setFreeTextData({});
      setMapData('');
      setFile(null);
    } catch (err) {
      setError(err.message || 'Error submitting form');
    } finally {
      setLoading(false);
    }
  };

  // Render structured field based on type
  const renderStructuredField = (field) => {
    const value = structuredData[field.name] || field.default || '';

    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            id={field.name}
            value={value}
            onChange={(e) => handleStructuredFieldChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            required={field.required}
            className="form-control"
          />
        );

      case 'textarea':
        return (
          <textarea
            id={field.name}
            value={value}
            onChange={(e) => handleStructuredFieldChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            required={field.required}
            rows={field.rows || 3}
            className="form-control"
          />
        );

      case 'select':
        return (
          <select
            id={field.name}
            value={value}
            onChange={(e) => handleStructuredFieldChange(field.name, e.target.value)}
            required={field.required}
            className="form-control"
          >
            <option value="">Select {field.label}...</option>
            {field.options.map((option, idx) => (
              <option key={idx} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case 'date':
        return (
          <input
            type="date"
            id={field.name}
            value={value}
            onChange={(e) => handleStructuredFieldChange(field.name, e.target.value)}
            required={field.required}
            className="form-control"
          />
        );

      case 'datetime-local':
        return (
          <input
            type="datetime-local"
            id={field.name}
            value={value || (field.default === 'now' ? new Date().toISOString().slice(0, 16) : '')}
            onChange={(e) => handleStructuredFieldChange(field.name, e.target.value)}
            required={field.required}
            className="form-control"
          />
        );

      case 'checkbox':
        return (
          <div className="checkbox-wrapper">
            <input
              type="checkbox"
              id={field.name}
              checked={!!value}
              onChange={(e) => handleStructuredFieldChange(field.name, e.target.checked)}
              className="form-check-input"
            />
            <label htmlFor={field.name} className="form-check-label">
              {field.label}
            </label>
          </div>
        );

      case 'checkbox-group':
        return (
          <div className="checkbox-group">
            {field.options.map((option, idx) => {
              const checked = Array.isArray(value) ? value.includes(option) : false;
              return (
                <div key={idx} className="checkbox-wrapper">
                  <input
                    type="checkbox"
                    id={`${field.name}-${idx}`}
                    checked={checked}
                    onChange={(e) => {
                      const currentValue = Array.isArray(value) ? value : [];
                      const newValue = e.target.checked
                        ? [...currentValue, option]
                        : currentValue.filter(v => v !== option);
                      handleStructuredFieldChange(field.name, newValue);
                    }}
                    className="form-check-input"
                  />
                  <label htmlFor={`${field.name}-${idx}`} className="form-check-label">
                    {option}
                  </label>
                </div>
              );
            })}
          </div>
        );

      case 'month-range':
        return (
          <input
            type="text"
            id={field.name}
            value={value}
            onChange={(e) => handleStructuredFieldChange(field.name, e.target.value)}
            placeholder={field.placeholder || 'e.g., June-August'}
            required={field.required}
            className="form-control"
          />
        );

      default:
        return (
          <input
            type="text"
            id={field.name}
            value={value}
            onChange={(e) => handleStructuredFieldChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            required={field.required}
            className="form-control"
          />
        );
    }
  };

  return (
    <div className="dynamic-form-container">
      <div className="form-header">
        <h2>{dataTypeConfig.icon} {dataTypeConfig.prompts.welcome}</h2>
        {dataTypeConfig.description && (
          <p className="form-description">{dataTypeConfig.description}</p>
        )}
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="dynamic-form">
        {/* Icon Palette Section */}
          {isHarvestingForm && (
            <div className="form-section">
              <h3>{dataTypeConfig.prompts.iconPrompt || 'Category'}</h3>

              <div className="form-group">
                <label htmlFor="harvesting-category">
                  Category
                  <span className="required-indicator">*</span>
                </label>
                <select
                  id="harvesting-category"
                  value={selectedHarvestCategoryName}
                  onChange={(e) => setHarvestCategory(e.target.value)}
                  required
                  className="form-control"
                  disabled={loading || harvestCategoriesLoading}
                >
                  <option value="">
                    {harvestCategoriesLoading ? 'Loading categories...' : 'Select category...'}
                  </option>
                  {harvestCategories.map((category, index) => (
                    <option key={`${category?.name || 'category'}-${index}`} value={normalizeString(category?.name || '')}>
                      {category?.icon ? `${category.icon} ` : ''}
                      {category?.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="harvesting-subcategory">Method / Context</label>
                <select
                  id="harvesting-subcategory"
                  value={selectedSubcategoryName}
                  onChange={(e) => setHarvestSubcategory(e.target.value)}
                  className="form-control"
                  disabled={loading || !selectedHarvestCategoryName || availableSubcategories.length === 0}
                >
                  <option value="">None / General</option>
                  {availableSubcategories.map((subcategory, index) => (
                    <option
                      key={`${subcategory?.name || 'subcategory'}-${index}`}
                      value={normalizeString(subcategory?.name || '')}
                    >
                      {subcategory?.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {dataTypeConfig.iconPalette && !isHarvestingForm && (
          <div className="form-section">
            <h3>{dataTypeConfig.prompts.iconPrompt || 'Select Type'}</h3>
            <IconPalette
              config={dataTypeConfig.iconPalette}
              value={selectedIcon}
              onChange={setSelectedIcon}
              disabled={loading}
            />
          </div>
        )}

        {/* Location/Map Section */}
        <div className="form-section">
          <h3>{dataTypeConfig.prompts.location}</h3>
          <div className="map-data-container">
            <button
              type="button"
              onClick={openLocationPicker}
              className="location-picker-btn"
              disabled={loading}
            >
              📍 {mapData ? 'Edit Location' : 'Select Location on Map'}
            </button>
            {mapData && (
              <div className="map-data-preview">
                <label>Map Data (GeoJSON):</label>
                <textarea
                  value={typeof mapData === 'string' ? mapData : JSON.stringify(mapData, null, 2)}
                  readOnly
                  rows={4}
                  className="form-control map-data-display"
                />
              </div>
            )}
          </div>
        </div>

        {/* Structured Fields Section */}
        {dataTypeConfig.structuredFields && dataTypeConfig.structuredFields.length > 0 && (
          <div className="form-section">
            <h3>{dataTypeConfig.prompts.details || 'Details'}</h3>
            {dataTypeConfig.structuredFields.map((field, idx) => (
              <div key={idx} className="form-group">
                {field.type !== 'checkbox' && (
                  <label htmlFor={field.name}>
                    {field.label}
                    {field.required && <span className="required-indicator">*</span>}
                  </label>
                )}
                {renderStructuredField(field)}
              </div>
            ))}
          </div>
        )}

        {/* Free-Text Fields Section */}
        {dataTypeConfig.freeTextFields && dataTypeConfig.freeTextFields.length > 0 && (
          <div className="form-section">
            <h3>Additional Information</h3>
            {dataTypeConfig.freeTextFields.map((field, idx) => (
              <div key={idx} className="form-group">
                <label htmlFor={field.name}>
                  {field.label}
                  {field.required && <span className="required-indicator">*</span>}
                </label>
                <textarea
                  id={field.name}
                  value={freeTextData[field.name] || ''}
                  onChange={(e) => handleFreeTextFieldChange(field.name, e.target.value)}
                  placeholder={field.placeholder}
                  rows={field.rows || 3}
                  required={field.required}
                  className="form-control"
                />
              </div>
            ))}
          </div>
        )}

        {/* File Upload Section */}
        {dataTypeConfig.fileUpload?.enabled && (
          <div className="form-section">
            <h3>{dataTypeConfig.fileUpload.label || 'Upload File'}</h3>
            <div className="form-group">
              <input
                type="file"
                accept={dataTypeConfig.fileUpload.accept}
                onChange={handleFileChange}
                className="form-control-file"
                multiple={dataTypeConfig.fileUpload.multiple}
              />
              {dataTypeConfig.fileUpload.maxFiles && (
                <small className="form-text text-muted">
                  Maximum {dataTypeConfig.fileUpload.maxFiles} file{dataTypeConfig.fileUpload.maxFiles > 1 ? 's' : ''}
                </small>
              )}
            </div>
          </div>
        )}

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            onClick={onCancel}
            className="btn btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Submitting...' : (initialData ? 'Update' : 'Submit')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DynamicDataCollectionForm;
